import {
  Box,
  Button,
  Card,
  ChakraProvider,
  Container,
  Grid,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  SimpleGrid,
  Spacer,
  Stack,
  Text,
  useMediaQuery,
} from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowLeftCircle,
  ArrowRight,
  ArrowRightCircle,
  Menu,
  Search,
} from "react-feather";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchVaultResources } from "../../api/lucroVault";
import { fetchTags } from "../../api/resources";
import theme from "../../theme";
import ClientDashboardLayout from "../dashboard/dashboardLayout";
import ResourceCard from "./resourceCard";
import { VideoPlayerPage } from "./videoPlayerPage";
import { WorkshopVault } from "./workshopListing";

export const ResourceVault = ({ componentOnly = false }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const containerRef = useRef(null);
  const [tagList, setTagList] = useState([]);
  const [cardList, setCardList] = useState([]);
  const [cardDownloadableList, setCardDownloadableList] = useState([]);
  const [category, setCategory] = useState("video");
  const [selectedTags, setSelectedTags] = useState(
    searchParams.get("tag") ? [Number(searchParams.get("tag"))] : []
  );
  const [view, setView] = useState("all");
  const [search, setSearch] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [prevPage, setPrevPage] = useState(null);
  const [nextPage, setNextPage] = useState(null);
  const [pageRange, setPageRange] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [prevPageVideo, setPrevPageVideo] = useState(null);
  const [nextPageVideo, setNextPageVideo] = useState(null);
  const [pageRangeVideo, setPageRangeVideo] = useState([]);
  const [currentPageVideo, setCurrentPageVideo] = useState(1);
  const [resourceData, setResourceData] = useState({});
  const [toggleView, setToggleView] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    setIsMobileDevice(
      /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
    );
  }, [setIsMobileDevice]);

  const scrollRef = useRef(null);

  const resourceId = useMemo(
    () => searchParams.get("id") || null,
    [searchParams]
  );

  const [loadedId, setLoadedId] = useState(null);

  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const searchRef = useRef(location.search);
  useEffect(() => {
    searchRef.current = location.search;
  }, [location.search]);

  useEffect(() => {
    const id = resourceId;

    if (id && id !== loadedId) {
      fetchVaultResources(category, view, { id }).then((res) => {
        setResourceData(res?.results?.[0]);
        setToggleView(true);
        setLoadedId(id);
      });
    } else if (!id && loadedId) {
      setResourceData(null);
      setToggleView(false);
      setLoadedId(null);
    }
  }, [resourceId, category, view, loadedId]);

  useEffect(() => {
    if (!resourceData?.id) return;

    const currentParams = new URLSearchParams(searchRef.current);
    const currentId = currentParams.get("id");
    const newId = String(resourceData.id);

    if (currentId !== newId) {
      currentParams.set("id", newId);
      navigateRef.current({ search: currentParams.toString() }); // push (or { replace:true })
    }

    setToggleView(true);
  }, [resourceData]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await fetchTags();
      setTagList(data);
    };
    fetchData();
  }, []);

  const refreshDownloadableData = async (page) => {
    try {
      setCurrentPage(page);

      const params = {
        tags: selectedTags,
        searchTerm: search,
        page,
      };

      const res = await fetchVaultResources(category, view, params);

      setCardDownloadableList(res);
      setPageRange(res.page_range);

      if (res.next) {
        const nextUrlObj = new URL(res.next);
        setNextPage(parseInt(nextUrlObj.searchParams.get("page"), 10));
      } else {
        setNextPage(null);
      }

      if (res.previous) {
        const prevUrlObj = new URL(res.previous);
        setPrevPage(parseInt(prevUrlObj.searchParams.get("page"), 10) || 1);
      } else {
        setPrevPage(null);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const refreshVideoData = async (page) => {
    try {
      setCurrentPageVideo(page);

      const params = {
        tags: selectedTags,
        searchTerm: search,
        page: page,
      };

      const res = await fetchVaultResources(category, view, params);

      setCardList(res);
      setPageRangeVideo(res.page_range);

      if (res.next) {
        const nextUrlObj = new URL(res.next);
        setNextPageVideo(parseInt(nextUrlObj.searchParams.get("page"), 10));
      } else {
        setNextPageVideo(null);
      }

      if (res.previous) {
        const prevUrlObj = new URL(res.previous);
        setPrevPageVideo(
          parseInt(prevUrlObj.searchParams.get("page"), 10) || 1
        );
      } else {
        setPrevPageVideo(null);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    const params = {
      searchTerm: search,
      tags: selectedTags,
    };

    setPageRange([]);
    setNextPage(null);
    setPrevPage(null);
    setPageRangeVideo([]);
    setNextPageVideo(null);
    setPrevPageVideo(null);
    setCurrentPage(1);
    setCurrentPageVideo(1);

    const refreshResources = async () => {
      try {
        if (category === "overview") {
          const [downloadableRes, videoRes] = await Promise.all([
            fetchVaultResources("downloadable", "overview", params),
            fetchVaultResources("video", "overview", params),
          ]);

          setCardDownloadableList(downloadableRes);
          setCardList(videoRes);
        } else {
          if (category === "downloadable") {
            const res = await fetchVaultResources(category, view, params);
            setCardDownloadableList(res);
            setPageRange(res.page_range);

            if (res.next) {
              const nextUrlObj = new URL(res.next);
              setNextPage(parseInt(nextUrlObj.searchParams.get("page"), 10));
            } else {
              setNextPage(null);
            }

            if (res.previous) {
              const prevUrlObj = new URL(res.previous);
              setPrevPage(
                parseInt(prevUrlObj.searchParams.get("page"), 10) || 1
              );
            } else {
              setPrevPage(null);
            }
          } else {
            setCardDownloadableList([]);
          }

          if (category === "video") {
            const res = await fetchVaultResources(category, view, params);
            setCardList(res);
            setPageRangeVideo(res.page_range);

            if (res.next) {
              const nextUrlObj = new URL(res.next);
              setNextPageVideo(
                parseInt(nextUrlObj.searchParams.get("page"), 10)
              );
            } else {
              setNextPageVideo(null);
            }

            if (res.previous) {
              const prevUrlObj = new URL(res.previous);
              setPrevPageVideo(
                parseInt(prevUrlObj.searchParams.get("page"), 10) || 1
              );
            } else {
              setPrevPageVideo(null);
            }
          } else {
            setCardList([]);
          }
        }
      } catch (err) {
        console.log(err);
      }
    };

    refreshResources();
  }, [category, selectedTags, search, view]);

  const scrollTabs = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = 150;
    scrollRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const handleSidebarExpand = () => {
      setExpanded(true)
    };

    const handleSidebarCollapse = () => {
      setExpanded(false)
    };

    window.addEventListener("sidebar:expand", handleSidebarExpand);
    window.addEventListener("sidebar:collapse", handleSidebarCollapse);

    return () => {
      window.removeEventListener("sidebar:expand", handleSidebarExpand);
      window.removeEventListener("sidebar:collapse", handleSidebarCollapse);
    };
  }, []);

  const [isMobile] = useMediaQuery("(max-width: 1280px)");
  const [navOverlap] = useMediaQuery("(max-width: 1280px)", { ssr: false });

  return !componentOnly ? (
    <ClientDashboardLayout>
      <VaultContent
        isMobile={isMobile}
        isMobileDevice={isMobileDevice}
        toggleView={toggleView}
        navOverlap={navOverlap}
        containerRef={containerRef}
        scrollRef={scrollRef}
        inputValue={inputValue}
        setInputValue={setInputValue}
        search={search}
        setSearch={setSearch}
        category={category}
        setCategory={setCategory}
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
        view={view}
        setView={setView}
        resourceData={resourceData}
        setResourceData={setResourceData}
        location={location}
        navigate={navigate}
        prevPage={prevPage}
        nextPage={nextPage}
        prevPageVideo={prevPageVideo}
        nextPageVideo={nextPageVideo}
        currentPage={currentPage}
        currentPageVideo={currentPageVideo}
        pageRange={pageRange}
        pageRangeVideo={pageRangeVideo}
        cardList={cardList}
        cardDownloadableList={cardDownloadableList}
        tagList={tagList}
        refreshDownloadableData={refreshDownloadableData}
        refreshVideoData={refreshVideoData}
        scrollTabs={scrollTabs}
        setToggleView={setToggleView}
        expanded={expanded}
      />
    </ClientDashboardLayout>
  ) : (
    <VaultContent
      isMobile={isMobile}
      isMobileDevice={isMobileDevice}
      toggleView={toggleView}
      navOverlap={navOverlap}
      containerRef={containerRef}
      scrollRef={scrollRef}
      inputValue={inputValue}
      setInputValue={setInputValue}
      search={search}
      setSearch={setSearch}
      category={category}
      setCategory={setCategory}
      selectedTags={selectedTags}
      setSelectedTags={setSelectedTags}
      view={view}
      setView={setView}
      resourceData={resourceData}
      setResourceData={setResourceData}
      location={location}
      navigate={navigate}
      prevPage={prevPage}
      nextPage={nextPage}
      prevPageVideo={prevPageVideo}
      nextPageVideo={nextPageVideo}
      currentPage={currentPage}
      currentPageVideo={currentPageVideo}
      pageRange={pageRange}
      pageRangeVideo={pageRangeVideo}
      cardList={cardList}
      cardDownloadableList={cardDownloadableList}
      tagList={tagList}
      refreshDownloadableData={refreshDownloadableData}
      refreshVideoData={refreshVideoData}
      scrollTabs={scrollTabs}
      setToggleView={setToggleView}
      expanded={expanded}
    />
  );
};

const VaultContent = ({
  isMobile,
  isMobileDevice,
  toggleView,
  navOverlap,
  containerRef,
  scrollRef,
  inputValue,
  setInputValue,
  search,
  setSearch,
  category,
  setCategory,
  selectedTags,
  setSelectedTags,
  view,
  setView,
  resourceData,
  setResourceData,
  location,
  navigate,
  prevPage,
  nextPage,
  prevPageVideo,
  nextPageVideo,
  currentPage,
  currentPageVideo,
  pageRange,
  pageRangeVideo,
  cardList,
  cardDownloadableList,
  tagList,
  refreshDownloadableData,
  refreshVideoData,
  scrollTabs,
  setToggleView,
  expanded
}) => {
  return (
    <ChakraProvider theme={theme}>
      <Container
        ref={containerRef}
        width={"100%"}
        maxW="100%"
        bg="#F4F4F2"
        minH={"100vh"}
        p="0px"
        overflowX={"hidden"}
        position={"relative"}
      >
        <Stack width={"100%"}>
          {isMobile && (
            <Card
              position="fixed"
              zIndex={600}
              borderRadius={0}
              h="72px"
              borderBottom="1px solid #e0e0e0"
              width="100%"
            >
              <HStack
                justifyContent="space-between"
                alignItems="center"
                h={"100%"}
                w="100%"
                px={4}
              >
                <Button
                  bg="white"
                  _hover={{ color: "white" }}
                  _active={{ color: "white" }}
                  w="50px"
                  h="50px"
                  p={2}
                  onClick={() => {
                    window.dispatchEvent(new Event("sidebar:expand"));
                  }}
                >
                  <Menu
                    style={{ width: "50px", height: "100%" }}
                    color="black"
                  />
                </Button>
                <Spacer />
                <InputGroup width="230px" height="36px" mr={"7px"}>
                  <Input
                    height="36px"
                    fontSize={"14px"}
                    placeholder="Search Resources..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                  />
                  <InputRightElement width="44px" height="36px">
                    <Button
                      width="44px"
                      height="36px"
                      p={0}
                      minW="unset"
                      borderLeftRadius={0}
                      onClick={() => {
                        setSearch(inputValue);
                        setCategory("video");
                        setToggleView(false);
                        if (location.search) {
                          navigate(location.pathname);
                        }
                      }}
                    >
                      <Search size="20px" />
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </HStack>
            </Card>
          )}
          {!isMobile && (
            <Card
              position="fixed"
              mt={isMobile ? "72px" : "0px"}
              borderRadius={0}
              width={isMobile ? "100%" : "auto"}
              height="75px"
              display="flex"
              alignItems="center"
              zIndex={600}
              borderBottom="1px solid #e0e0e0"
              mb={1}
            >
              <Grid
                templateColumns="1fr 200fr 1fr"
                width="100%"
                maxW={"100%"}
                alignItems="center"
                padding="0px 20px"
                height="100%"
              >
                <Box
                  gridRow={1}
                  gridColumn={1}
                  textAlign={"left"}
                  fontWeight={700}
                  fontSize={"26px"}
                  whiteSpace={"nowrap"}
                >
                  The Lucro Vault
                </Box>
                <Box
                  gridRow={1}
                  gridColumn={3}
                  display="flex"
                  justifyContent="flex-end"
                >
                  <HStack>
                    <>
                      <InputGroup width="320px" height="36px" mr={"7px"}>
                        <Input
                          height="36px"
                          placeholder="Search Resources..."
                          value={inputValue}
                          onChange={(e) => setInputValue(e.target.value)}
                        />
                        <InputRightElement width="44px" height="36px">
                          <Button
                            width="44px"
                            height="36px"
                            p={0}
                            minW="unset"
                            borderLeftRadius={0}
                            onClick={() => {
                              setSearch(inputValue);
                              setCategory("video");
                              setToggleView(false);
                              if (location.search) {
                                navigate(location.pathname);
                              }
                            }}
                          >
                            <Search size="24px" />
                          </Button>
                        </InputRightElement>
                      </InputGroup>
                    </>
                  </HStack>
                </Box>
              </Grid>
            </Card>
          )}
          <Stack mt={isMobile ? "72px" : "72px"} height="100%">
            {toggleView &&
              (resourceData?.is_workshop ? (
                <WorkshopVault expanded={expanded} resourceData={resourceData} />
              ) : (
                <VideoPlayerPage
                  resourceData={resourceData}
                  setVault={setToggleView}
                />
              ))}

            {!toggleView && (
              <>
                <Box
                  backgroundColor={"#F4F4F2"}
                  position="fixed"
                  zIndex={400}
                  p={{
                    base: "12px 24px",
                    sm: "12px 24px",
                    md: "22px 24x",
                    lg: "24px 24px",
                  }}
                  width={isMobile ? "100%" : "auto"}
                  minWidth="100%"
                  maxW="100%"
                  sx={{
                    WebkitMaskImage:
                      "linear-gradient(to bottom, black 90%, transparent 100%)",
                    maskImage:
                      "linear-gradient(to bottom, black 90%, transparent 100%)",
                  }}
                >
                  <Grid
                    templateColumns="1fr"
                    width={"100%"}
                    height="100%"
                  ></Grid>
                  <HStack pb="10px" width="100%" justifyContent="space-between">
                    <Text
                      fontWeight={700}
                      fontSize={{
                        base: "20px",
                        sm: "20px",
                        md: "20px",
                        lg: "24px",
                      }}
                      pl="10px"
                    >
                      Growth Tools
                    </Text>

                    {!isMobileDevice && (
                      <HStack
                        pr="10px"
                        mr={
                        !isMobile
                          ? expanded
                            ? navOverlap
                              ? "0px"
                              : "300px"
                            : "105px"
                          : "0px"
                      }
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          borderRadius="full"
                          transition="all 0.2s ease"
                          cursor="pointer"
                          _hover={{
                            bg: "gray.100",
                            transform: "scale(1.05)",
                          }}
                          _active={{
                            bg: "gray.200",
                            transform: "scale(0.95)",
                          }}
                          onClick={() => scrollTabs("left")}
                        >
                          <ArrowLeftCircle size={32} color="#536DEC" />
                        </Box>

                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          borderRadius="full"
                          transition="all 0.2s ease"
                          cursor="pointer"
                          _hover={{
                            bg: "gray.100",
                            transform: "scale(1.05)",
                          }}
                          _active={{
                            bg: "gray.200",
                            transform: "scale(0.95)",
                          }}
                          onClick={() => scrollTabs("right")}
                        >
                          <ArrowRightCircle size={32} color="#536DEC" />
                        </Box>
                      </HStack>
                    )}
                  </HStack>

                  <Grid
                    templateColumns="1fr"
                    width={
                    isMobile
                      ? "100%"
                      : expanded
                      ? navOverlap
                        ? "100%"
                        : "calc(100% - 300px)"
                      : "calc(100% - 105px)"
                  }
                    height="100%"
                  >
                    <Box
                      ref={scrollRef}
                      overflowX="auto"
                      whiteSpace="nowrap"
                      flex="1"
                      sx={{
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                        "&::-webkit-scrollbar": {
                          display: "none",
                        },
                      }}
                    >
                      <HStack as="div" spacing="15px">
                        <Button
                          flexShrink={0}
                          fontWeight={600}
                          padding="10px 15px"
                          borderRadius="6px"
                          fontSize={{
                            base: "14px",
                            sm: "14px",
                            md: "16px",
                            lg: "16px",
                          }}
                          bg={selectedTags.length === 0 ? "#536DEC" : "white"}
                          color={selectedTags.length === 0 ? "white" : "black"}
                          _hover={{ bg: "#536DEC", color: "white" }}
                          onClick={() => {
                            setSelectedTags([]);
                            setCategory("video");
                          }}
                        >
                          Overview
                        </Button>

                        {tagList.map((tag) => {
                          const isSelected = selectedTags.includes(tag.id);
                          return (
                            <Button
                              key={tag.id}
                              flexShrink={0}
                              fontWeight={600}
                              padding="10px 15px"
                              borderRadius="md"
                              fontSize={{
                                base: "14px",
                                sm: "14px",
                                md: "16px",
                                lg: "16px",
                              }}
                              bg={isSelected ? "#536DEC" : "white"}
                              color={isSelected ? "white" : "black"}
                              _hover={{ bg: "#536DEC", color: "white" }}
                              onClick={() => {
                                setCategory("video");
                                setSelectedTags((prev) =>
                                  isSelected
                                    ? prev.filter((id) => id !== tag.id)
                                    : [...prev, tag.id]
                                );
                              }}
                            >
                              {tag.name}
                            </Button>
                          );
                        })}
                      </HStack>
                    </Box>
                  </Grid>
                </Box>
                <Stack
                  p={"24px 24px"}
                  mt={{
                    base: "100px",
                    sm: "100px",
                    md: "105px",
                    lg: "110px",
                  }}
                >
                  {cardList?.results?.length > 0 && (
                    <Stack>
                      {(category === "video" || category === "overview") && (
                        <HStack>
                          <Text
                            pl={"10px"}
                            pb={"10px"}
                            pt={"10px"}
                            fontWeight={600}
                            fontSize={"20px"}
                          >
                            Video Resources
                          </Text>
                          <Spacer />
                          {category === "overview" && cardList?.count === 9 && (
                            <Text
                              mr={"10px"}
                              fontWeight={500}
                              fontSize={"16px"}
                              color={"#536DEC"}
                              cursor={"pointer"}
                              onClick={() => {
                                setCategory("video");
                                setView("all");
                              }}
                            >
                              See all
                            </Text>
                          )}
                        </HStack>
                      )}

                      <SimpleGrid
                        columns={{ base: 1, ms: 2, lx: 3 }}
                        spacing="12px 20px"
                      >
                        {cardList?.results?.map((card, index) => (
                          <ResourceCard
                            key={index}
                            tags={card?.tags}
                            type={card?.resource_type}
                            title={card?.resource_title}
                            description={card?.resource_description}
                            resourceVideoLink={card?.external_link}
                            resourceLink={card?.resource_file}
                            backgroundImage={card?.card_image}
                            videoDuration={card?.video_duration}
                            resourceData={card}
                            setSelectedResource={setResourceData}
                            toggleView={setToggleView}
                          />
                        ))}
                      </SimpleGrid>
                    </Stack>
                  )}

                  {cardDownloadableList?.results?.length > 0 && (
                    <Stack>
                      {(category === "downloadable" ||
                        category === "overview") && (
                        <HStack>
                          <Text
                            pl={"10px"}
                            pb={"10px"}
                            pt={"10px"}
                            fontWeight={600}
                            fontSize={"20px"}
                          >
                            Reading Resources
                          </Text>
                          <Spacer />
                          {category === "overview" &&
                            cardDownloadableList?.count === 9 && (
                              <Text
                                mr={"10px"}
                                fontWeight={500}
                                fontSize={"16px"}
                                color={"#536DEC"}
                                cursor={"pointer"}
                                onClick={() => {
                                  setCategory("downloadable");
                                  setView("all");
                                }}
                              >
                                See all
                              </Text>
                            )}
                        </HStack>
                      )}
                      <SimpleGrid
                        columns={{ base: 1, ms: 2, lx: 3 }}
                        spacing="12px 20px"
                      >
                        {cardDownloadableList?.results?.map((card, index) => (
                          <ResourceCard
                            key={index}
                            tags={card?.tags}
                            title={card?.resource_title}
                            description={card?.resource_description}
                            resourceVideoLink={card?.external_link}
                            resourceLink={card?.resource_file}
                            backgroundImage={card?.card_image}
                          />
                        ))}
                      </SimpleGrid>
                    </Stack>
                  )}
                  {category === "downloadable" && (
                    <HStack
                      pt={"10px"}
                      spacing={!isMobile ? 3 : 0}
                      justify="center"
                      align="center"
                    >
                      <Button
                        variant="unstyled"
                        isDisabled={!prevPage}
                        _disabled={{
                          cursor: "default",
                          opacity: 0.6,
                          pointerEvents: "none",
                        }}
                        cursor={!prevPage ? "default" : "pointer"}
                        onClick={() => refreshDownloadableData(prevPage)}
                      >
                        <HStack>
                          {!isMobile && <ArrowLeft />}
                          <Text>Prev</Text>
                        </HStack>
                      </Button>
                      {pageRange.map((item, idx) =>
                        typeof item === "number" ? (
                          <Button
                            key={idx}
                            variant="unstyled"
                            onClick={() => refreshDownloadableData(item)}
                            textColor={item === currentPage ? "white" : "black"}
                            backgroundColor={
                              item === currentPage ? "#536DEC" : "white"
                            }
                            _hover={{ color: "#627CFB" }}
                          >
                            {item}
                          </Button>
                        ) : (
                          <Text key={idx} color="black">
                            ...
                          </Text>
                        )
                      )}

                      <Button
                        variant="unstyled"
                        isDisabled={!nextPage}
                        _disabled={{
                          cursor: "default",
                          opacity: 0.6,
                          pointerEvents: "none",
                        }}
                        cursor={!nextPage ? "default" : "pointer"}
                        onClick={() => refreshDownloadableData(nextPage)}
                      >
                        <HStack>
                          <Text>Next</Text>
                          {!isMobile && <ArrowRight />}
                        </HStack>
                      </Button>
                    </HStack>
                  )}

                  {category === "video" && (
                    <HStack
                      pt={"10px"}
                      spacing={!isMobile ? 3 : 0}
                      justify="center"
                      align="center"
                    >
                      <Button
                        variant="unstyled"
                        isDisabled={!prevPageVideo}
                        _disabled={{
                          cursor: "default",
                          opacity: 0.6,
                          pointerEvents: "none",
                        }}
                        cursor={!prevPageVideo ? "default" : "pointer"}
                        onClick={() => refreshVideoData(prevPageVideo)}
                      >
                        <HStack>
                          {!isMobile && <ArrowLeft />}
                          <Text>Prev</Text>
                        </HStack>
                      </Button>
                      {pageRangeVideo.map((item, idx) =>
                        typeof item === "number" ? (
                          <Button
                            key={idx}
                            variant="unstyled"
                            onClick={() => refreshVideoData(item)}
                            textColor={
                              item === currentPageVideo ? "white" : "black"
                            }
                            backgroundColor={
                              item === currentPageVideo ? "#536DEC" : "white"
                            }
                            _hover={{ color: "#627CFB" }}
                          >
                            {item}
                          </Button>
                        ) : (
                          <Text key={idx} color="black">
                            ...
                          </Text>
                        )
                      )}

                      <Button
                        variant="unstyled"
                        isDisabled={!nextPageVideo}
                        _disabled={{
                          cursor: "default",
                          opacity: 0.6,
                          pointerEvents: "none",
                        }}
                        cursor={!nextPageVideo ? "default" : "pointer"}
                        onClick={() => refreshVideoData(nextPageVideo)}
                      >
                        <HStack>
                          <Text>Next</Text>
                          {!isMobile && <ArrowRight />}
                        </HStack>
                      </Button>
                    </HStack>
                  )}
                </Stack>
              </>
            )}
          </Stack>
        </Stack>
      </Container>
    </ChakraProvider>
  );
};
